import { pick, includes } from 'ramda';

import getParcelNumber from '../../utils/getParcelNumber';
import { joinArrayToString } from '../../utils/string';
import formatPostcode from '../../utils/formatPostcode';
import {
  SATURDAY_SERVICE_SHORT,
  SUNDAY_SERVICE_SHORT,
  DRIVER_REQUEST_TABLE_TITLE,
  DUTY,
  LOCKED,
  OT,
} from '../../constants/strings';
import { getRequestReason, getStopByNumber } from '../routeDetail/model';
import { requestTypes, DRIVER_REQUEST_TYPE } from '../../enums/requestTypes';
import {
  requestedParcelsColumns,
  requestedCollectionColumns,
  pickupRequestColumns,
} from './tableConfigs';
import { headerCellStyles, bodyCellStyles } from './tableStyles';
import {
  requestedParcelsColumnsConfigs,
  requestedCollectionColumnsConfigs,
  pickupRequestColumnsConfigs,
} from '../../enums/columnsConfigs';
import { formatSecondsToHHMM } from '../../utils/date';
import { getServiceWindow } from '../../models/stop';
import { isRouteStatusInBuild } from '../../models/routeDetail';

const getPickupStopNumber = (request, route) => {
  const maxSequenceNumber = Math.max(
    ...route.stop.map(({ sequenceNumber }) => sequenceNumber)
  );

  return request.address ? maxSequenceNumber + 1 : '';
};

export const getAddress = (addressObj) =>
  joinArrayToString([
    addressObj?.contactName,
    addressObj?.address1,
    addressObj?.address2,
  ]).toUpperCase();

export const getAddressWithPostcode = (addressObj) =>
  joinArrayToString([
    addressObj?.contactName,
    addressObj?.address1,
    addressObj?.address2,
    formatPostcode(addressObj?.postcode),
  ]).toUpperCase();

const getServiceLabel = (request) =>
  request.saturdayService
    ? SATURDAY_SERVICE_SHORT
    : request.sundayService
      ? SUNDAY_SERVICE_SHORT
      : '';

export const prepareRequestParcels = (stop, route) => {
  if (!stop?.requests?.length) {
    return [];
  }

  const stopParcels =
    getStopByNumber(route.stop, stop.stopNumber)?.parcel || [];

  return stop.requests.map((request) => ({
    ...request,
    loaded: request.loaded,
    cadScanned: request.cadScan,
    parcelStatus: request.parcelStatus,
    requestType: request.requestType,
    parcelNumber: getParcelNumber(request.labelNumber),
    displayServiceWindow: true,
    postcode: request.address?.postcode || stop.postcode,
    address:
      request.requestType === requestTypes.AMEND_PICKUP_PARCELS &&
      !request.address
        ? stop.address
        : getAddressWithPostcode(request.address),
    service: {
      perishable: request.perishable,
      vipDelivery: request.vipDelivery,
      serviceLabel: getServiceLabel(request),
    },
    window: getServiceWindow({
      ...pick(['serviceStartTime', 'serviceEndTime'], request),
      displayServiceWindow: true,
    }),
    requestReason: getRequestReason(request),
    stopNumber: getPickupStopNumber(request, route),
    collectionType: request.collectionType,
    totalParcels: stop.requests.length,
    totalCadParcels: stop.totalCadParcels,
    exception: stop.exception,
    chute: stopParcels.find((p) => p.parcelCode === request.parcelCode)?.chute,
  }));
};

export const getRequestType = ({ requests }) => {
  switch (requests[0].requestType) {
    case requestTypes.ADD_COLLECTION:
    case requestTypes.REMOVE_COLLECTION:
      return DRIVER_REQUEST_TYPE.COLLECTION;
    case requestTypes.AMEND_PICKUP_PARCELS:
      return DRIVER_REQUEST_TYPE.PICKUP;
    default:
      return DRIVER_REQUEST_TYPE.DELIVERY;
  }
};

export const getDriverRequestTableTitle = (stop) => {
  const driverRequestType = getRequestType(stop);
  if (driverRequestType === DRIVER_REQUEST_TYPE.COLLECTION) {
    return DRIVER_REQUEST_TABLE_TITLE.REQUESTED_COLLECTIONS;
  } else {
    return DRIVER_REQUEST_TABLE_TITLE.REQUESTED_PARCELS;
  }
};

export const getTableColumnsConfig = (stop) => {
  const driverRequestType = getRequestType(stop);
  let config;
  let columnsConfigs;
  if (driverRequestType === DRIVER_REQUEST_TYPE.COLLECTION) {
    config = requestedCollectionColumns;
    columnsConfigs = requestedCollectionColumnsConfigs;
  } else {
    config = requestedParcelsColumns;
    columnsConfigs = requestedParcelsColumnsConfigs;
  }
  return config({
    headerCellClasses: headerCellStyles(),
    bodyCellClasses: bodyCellStyles(),
    columnsConfigs,
  });
};

export const getAffectedRequestsTableColumnsConfig = () =>
  requestedParcelsColumns({
    headerCellClasses: headerCellStyles(),
    bodyCellClasses: bodyCellStyles(),
    columnsConfigs: requestedParcelsColumnsConfigs,
  });

export const prepareTransferDriversRoutes = (
  routes,
  currentRouteId,
  uniqueNextWaveRoutes
) => {
  const formattedRoutes = routes
    .filter(
      (route) =>
        route.routeId !== currentRouteId &&
        !isRouteStatusInBuild({ status: route.routeStatus })
    )
    .sort((a, b) => (a.routeCode > b.routeCode ? 1 : -1))
    .map((route) => {
      const {
        routeId,
        routeCode,
        driverName,
        routeStatusDescription,
        routeLockedBy,
        dutyTimeExceptions,
        opportunityTime,
      } = route;

      const lockedBy = routeLockedBy ? LOCKED : `${dutyTimeExceptions} ${DUTY}`;

      const formattedOpportunityTime = formatSecondsToHHMM(opportunityTime);

      return {
        value: routeId,
        label: `${routeCode} - ${driverName} - ${OT} ${formattedOpportunityTime} - ${routeStatusDescription} - ${lockedBy}`,
        locked: routeLockedBy,
        isSuggested: includes(routeCode, uniqueNextWaveRoutes),
      };
    });

  const suggestedOptions = formattedRoutes.filter((route) => route.isSuggested);

  const otherOptions = formattedRoutes.filter((route) => !route.isSuggested);

  return [...suggestedOptions, ...otherOptions];
};

export const getPickupColumnsConfig = () =>
  pickupRequestColumns({
    headerCellClasses: headerCellStyles(),
    bodyCellClasses: bodyCellStyles(),
    columnsConfigs: pickupRequestColumnsConfigs,
  });

export const getPickupTableTitleSubtitle = (stop) =>
  stop.requests[0].address
    ? {
        title: DRIVER_REQUEST_TABLE_TITLE.NEW_STOP_RECIPIENT_ADDRESS,
        subtitle:
          DRIVER_REQUEST_TABLE_TITLE.NEW_STOP_RECIPIENT_ADDRESS_SUBTITLE,
      }
    : {
        title: DRIVER_REQUEST_TABLE_TITLE.NEW_STOP_GOODS_ON_HOLD,
        subtitle: DRIVER_REQUEST_TABLE_TITLE.NEW_STOP_GOODS_ON_HOLD_SUBTITLE,
      };

export const getAffectedRequestsTableData = (requests, route) =>
  Object.values(requests).reduce(
    (acc, item) => [
      ...acc,
      ...prepareRequestParcels(
        { requests: item, stopNumber: item[0].stopNumber },
        route
      ),
    ],
    []
  );
